import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import {
  createPoolAlertDevice,
  deletePoolAlertDevice,
  fetchAlertDeviceTemplates,
  fetchPoolAlertDevicesAdmin,
  formatPoolAlertDeviceTestResult,
  testPoolAlertDevice,
  updatePoolAlertDevice,
  type AlertDeviceLinkMode,
  type AlertDeviceTemplate,
  type PoolAlertDeviceProtocol,
  type PoolAlertDeviceRecord,
  type PoolAlertDeviceType,
} from '../../services/poolAlertDeviceService';
import { DEFAULT_POOL_SIREN_API_CONFIG } from '../../constants/poolSirenApiConfig';
import {
  ETH_RELAY_TCP_HEX_SAMPLE_HOST,
  ETH_RELAY_TCP_HEX_SAMPLE_PORT,
  ETH_RELAY_TCP_SERVER_A_SAMPLE_HOST,
  ETH_RELAY_TCP_SERVER_A_SAMPLE_PORT,
  ethRelayTcpHexCommands,
  sampleAlertDeviceChannel,
  sampleAlertDeviceLabel,
} from '../../constants/poolAlertDeviceSamples';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';

type Props = {
  poolId: number | null;
  /** 이 탭에 표시·등록할 장비 그룹 */
  linkMode: AlertDeviceLinkMode;
  /** 시설에 현재 적용 중인 방식 */
  activeLinkMode?: AlertDeviceLinkMode;
  onActiveLinkModeChange?: (mode: AlertDeviceLinkMode) => Promise<void> | void;
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
};

type DeviceFormState = {
  device_type: PoolAlertDeviceType;
  label: string;
  host: string;
  port: string;
  location_hint: string;
  protocol: PoolAlertDeviceProtocol;
  template_key: string;
  tcp_append_newline: boolean;
  blink_enabled: boolean;
  tcp_command_start_danger: string;
  tcp_command_start_warning: string;
  tcp_command_stop: string;
  http_method: 'GET' | 'POST';
  http_start_path_danger: string;
  http_start_path_warning: string;
  http_stop_path: string;
  socket_timeout_sec: string;
  is_active: boolean;
  sort_order: string;
};

const emptyForm = (
  deviceType: PoolAlertDeviceType,
  linkMode: AlertDeviceLinkMode
): DeviceFormState => {
  const channel = sampleAlertDeviceChannel(deviceType);
  const cmds = ethRelayTcpHexCommands(channel);
  if (linkMode === 'tcp_server_a') {
    return {
      device_type: deviceType,
      label: sampleAlertDeviceLabel(deviceType, channel),
      host: ETH_RELAY_TCP_SERVER_A_SAMPLE_HOST,
      port: String(ETH_RELAY_TCP_SERVER_A_SAMPLE_PORT),
      location_hint: 'A모드 — 게이트웨이 listen 후 릴레이 전원 재기동',
      protocol: 'tcp_server',
      template_key: `eth_relay_tcp_server_hex_ch${channel}`,
      tcp_append_newline: false,
      blink_enabled: false,
      tcp_command_start_danger: cmds.start,
      tcp_command_start_warning: cmds.start,
      tcp_command_stop: cmds.stop,
      http_method: DEFAULT_POOL_SIREN_API_CONFIG.http_method,
      http_start_path_danger: '',
      http_start_path_warning: '',
      http_stop_path: '',
      socket_timeout_sec: String(DEFAULT_POOL_SIREN_API_CONFIG.socket_timeout_sec),
      is_active: true,
      sort_order: String(channel),
    };
  }
  return {
    device_type: deviceType,
    label: sampleAlertDeviceLabel(deviceType, channel),
    host: ETH_RELAY_TCP_HEX_SAMPLE_HOST,
    port: String(ETH_RELAY_TCP_HEX_SAMPLE_PORT),
    location_hint: '샘플 — 현장 LAN IP·채널을 확인',
    protocol: 'tcp',
    template_key: `eth_relay_tcp_hex_ch${channel}`,
    tcp_append_newline: false,
    blink_enabled: false,
    tcp_command_start_danger: cmds.start,
    tcp_command_start_warning: cmds.start,
    tcp_command_stop: cmds.stop,
    http_method: DEFAULT_POOL_SIREN_API_CONFIG.http_method,
    http_start_path_danger: DEFAULT_POOL_SIREN_API_CONFIG.http_start_path_danger,
    http_start_path_warning: DEFAULT_POOL_SIREN_API_CONFIG.http_start_path_warning,
    http_stop_path: DEFAULT_POOL_SIREN_API_CONFIG.http_stop_path,
    socket_timeout_sec: String(DEFAULT_POOL_SIREN_API_CONFIG.socket_timeout_sec),
    is_active: true,
    sort_order: String(channel),
  };
};

function displayNameSortKey(d: PoolAlertDeviceRecord): string {
  const name = (d.label || '').trim();
  if (name) return name;
  return d.display_label || d.host;
}

function formatListDisplayName(d: PoolAlertDeviceRecord): string {
  return displayNameSortKey(d);
}

function recordToForm(d: PoolAlertDeviceRecord): DeviceFormState {
  return {
    device_type: d.device_type,
    label: d.label ?? '',
    host: d.host,
    port: d.port != null ? String(d.port) : '',
    location_hint: d.location_hint ?? '',
    protocol: d.protocol || 'tcp',
    template_key: d.template_key ?? '',
    tcp_append_newline: d.tcp_append_newline,
    blink_enabled: d.blink_enabled === true,
    tcp_command_start_danger:
      d.tcp_command_start_danger ?? DEFAULT_POOL_SIREN_API_CONFIG.tcp_command_start_danger,
    tcp_command_start_warning:
      d.tcp_command_start_warning ?? DEFAULT_POOL_SIREN_API_CONFIG.tcp_command_start_warning,
    tcp_command_stop: d.tcp_command_stop ?? DEFAULT_POOL_SIREN_API_CONFIG.tcp_command_stop,
    http_method: d.http_method,
    http_start_path_danger:
      d.http_start_path_danger ?? DEFAULT_POOL_SIREN_API_CONFIG.http_start_path_danger,
    http_start_path_warning:
      d.http_start_path_warning ?? DEFAULT_POOL_SIREN_API_CONFIG.http_start_path_warning,
    http_stop_path: d.http_stop_path ?? DEFAULT_POOL_SIREN_API_CONFIG.http_stop_path,
    socket_timeout_sec:
      d.socket_timeout_sec != null
        ? String(d.socket_timeout_sec)
        : String(DEFAULT_POOL_SIREN_API_CONFIG.socket_timeout_sec),
    is_active: d.is_active,
    sort_order: String(d.sort_order ?? 0),
  };
}

/** 템플릿 키에서 채널 번호 추출 (…_ch3 → 3) */
function channelFromTemplateKey(key: string): number | null {
  const m = /(?:^|_)ch(\d+)$/i.exec(key.trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n < 1 || n > 8) return null;
  return n;
}

function applyTemplateToForm(
  f: DeviceFormState,
  key: string,
  tpl: AlertDeviceTemplate | undefined,
  deviceType: PoolAlertDeviceType
): DeviceFormState {
  if (!tpl || key === 'custom' || !key) {
    return { ...f, template_key: key };
  }
  const channel = channelFromTemplateKey(key);
  const next: DeviceFormState = {
    ...f,
    template_key: key,
    protocol: tpl.protocol,
    port: String(tpl.default_port ?? f.port),
    tcp_append_newline: tpl.tcp_append_newline,
    tcp_command_start_danger: tpl.tcp_command_start_danger ?? '',
    tcp_command_start_warning: tpl.tcp_command_start_warning ?? '',
    tcp_command_stop: tpl.tcp_command_stop ?? '',
    http_method: tpl.http_method,
    http_start_path_danger: tpl.http_start_path_danger ?? '',
    http_start_path_warning: tpl.http_start_path_warning ?? '',
    http_stop_path: tpl.http_stop_path ?? '',
    socket_timeout_sec: String(tpl.socket_timeout_sec ?? f.socket_timeout_sec),
  };
  if (channel != null) {
    next.label = sampleAlertDeviceLabel(deviceType, channel);
    next.sort_order = String(channel);
    if (tpl.protocol === 'tcp_server') {
      next.host = ETH_RELAY_TCP_SERVER_A_SAMPLE_HOST;
    }
  }
  return next;
}

function formToPayload(form: DeviceFormState): Partial<PoolAlertDeviceRecord> & {
  device_type: PoolAlertDeviceType;
  host: string;
} {
  const portNum = form.port.trim() === '' ? null : Number(form.port);
  const timeout =
    form.socket_timeout_sec.trim() === '' ? null : Number(form.socket_timeout_sec);
  return {
    device_type: form.device_type,
    label: form.label.trim() || null,
    host: form.host.trim(),
    port: Number.isFinite(portNum) ? portNum : null,
    location_hint: form.location_hint.trim() || null,
    protocol: form.protocol,
    template_key: form.template_key.trim() || null,
    tcp_append_newline: form.tcp_append_newline,
    blink_enabled: form.blink_enabled,
    tcp_command_start_danger: form.tcp_command_start_danger.trim() || null,
    tcp_command_start_warning: form.tcp_command_start_warning.trim() || null,
    tcp_command_stop: form.tcp_command_stop.trim() || null,
    http_method: form.http_method,
    http_start_path_danger: form.http_start_path_danger.trim() || null,
    http_start_path_warning: form.http_start_path_warning.trim() || null,
    http_stop_path: form.http_stop_path.trim() || null,
    socket_timeout_sec: Number.isFinite(timeout) ? timeout : null,
    is_active: form.is_active,
    sort_order: Number.isFinite(Number(form.sort_order)) ? Number(form.sort_order) : 0,
  };
}

const DEVICE_TYPE_LABEL: Record<PoolAlertDeviceType, string> = {
  siren: '경광등',
  buzzer: '부저',
};

export const PoolAlertDeviceManagement: React.FC<Props> = ({
  poolId,
  linkMode,
  activeLinkMode,
  onActiveLinkModeChange,
  showSnackbar,
}) => {
  const isServerA = linkMode === 'tcp_server_a';
  const [tab, setTab] = useState(0);
  const deviceType: PoolAlertDeviceType = tab === 0 ? 'siren' : 'buzzer';
  const [devices, setDevices] = useState<PoolAlertDeviceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<DeviceFormState>(() => emptyForm('siren', linkMode));
  const [saving, setSaving] = useState(false);
  const [modeSaving, setModeSaving] = useState(false);
  const [templates, setTemplates] = useState<AlertDeviceTemplate[]>([]);
  const [testDurationSec, setTestDurationSec] = useState('5');
  const [testingKey, setTestingKey] = useState<string | null>(null);

  const samplePort = isServerA
    ? ETH_RELAY_TCP_SERVER_A_SAMPLE_PORT
    : ETH_RELAY_TCP_HEX_SAMPLE_PORT;
  const appliedMode = activeLinkMode ?? 'client';
  const isThisModeActive = appliedMode === linkMode;

  const parseTestDuration = (): number | null => {
    const n = Number(testDurationSec);
    if (!Number.isFinite(n) || n < 1 || n > 300) return null;
    return Math.round(n);
  };

  const runTest = async (opts: { deviceId?: number; fromForm?: boolean }) => {
    if (!poolId) return;
    const duration = parseTestDuration();
    if (duration == null) {
      showSnackbar('테스트 울림 시간은 1~300초 사이로 입력', 'warning');
      return;
    }
    if (!opts.fromForm && opts.deviceId == null) return;
    if (opts.fromForm && !form.host.trim()) {
      showSnackbar('호스트(IP)를 입력한 뒤 테스트', 'warning');
      return;
    }
    const key = opts.fromForm ? 'form' : `row-${opts.deviceId}`;
    setTestingKey(key);
    try {
      const res = await testPoolAlertDevice({
        poolId,
        deviceId: opts.fromForm ? null : opts.deviceId,
        deviceType: opts.fromForm ? form.device_type : deviceType,
        durationSec: duration,
        alertLevel: 'warning',
        inlineConfig: opts.fromForm ? formToPayload(form) : undefined,
      });
      showSnackbar(formatPoolAlertDeviceTestResult(res), res.success ? 'success' : 'error');
    } catch (e: unknown) {
      showSnackbar(formatApiErrorMessage(e, '테스트에 실패'), 'error');
    } finally {
      setTestingKey(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchAlertDeviceTemplates(deviceType)
      .then((list) => {
        if (cancelled) return;
        const filtered = list.filter((t) =>
          isServerA ? t.protocol === 'tcp_server' : t.protocol !== 'tcp_server'
        );
        setTemplates(filtered);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceType, isServerA]);

  const load = useCallback(async () => {
    if (!poolId) {
      setDevices([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await fetchPoolAlertDevicesAdmin(poolId, deviceType, linkMode);
      setDevices(list);
    } catch (e: unknown) {
      setDevices([]);
      setError(formatApiErrorMessage(e, '장비 목록을 불러오기 실패'));
    } finally {
      setLoading(false);
    }
  }, [poolId, deviceType, linkMode]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(deviceType, linkMode));
    setDialogOpen(true);
  };

  const openEdit = (d: PoolAlertDeviceRecord) => {
    setEditingId(d.id);
    setForm(recordToForm(d));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!poolId) return;
    if (!form.host.trim()) {
      showSnackbar(
        isServerA ? 'listen bind(호스트)를 입력' : '호스트(IP)를 입력',
        'warning'
      );
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...formToPayload(form),
        protocol: isServerA ? ('tcp_server' as const) : form.protocol,
      };
      if (editingId != null) {
        await updatePoolAlertDevice(poolId, editingId, body);
        showSnackbar(`${DEVICE_TYPE_LABEL[deviceType]} 정보를 수정함`, 'success');
      } else {
        await createPoolAlertDevice(poolId, body);
        showSnackbar(`${DEVICE_TYPE_LABEL[deviceType]}을(를) 등록함`, 'success');
      }
      closeDialog();
      await load();
    } catch (e: unknown) {
      showSnackbar(formatApiErrorMessage(e, '저장에 실패'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyThisMode = async () => {
    if (!onActiveLinkModeChange || isThisModeActive) return;
    setModeSaving(true);
    try {
      await onActiveLinkModeChange(linkMode);
      showSnackbar(
        isServerA
          ? '이 시설 경광등/부저를 「경광등/부저 A」 방식으로 적용.'
          : '이 시설 경광등/부저를 「경광등/부저」(기존) 방식으로 적용.',
        'success'
      );
    } catch (e: unknown) {
      showSnackbar(formatApiErrorMessage(e, '적용 방식 저장에 실패'), 'error');
    } finally {
      setModeSaving(false);
    }
  };

  const handleDelete = async (d: PoolAlertDeviceRecord) => {
    if (!poolId) return;
    const label = d.display_label || d.host;
    if (!window.confirm(`「${label}」 장비 삭제 확인`)) return;
    try {
      await deletePoolAlertDevice(poolId, d.id);
      showSnackbar('삭제함', 'success');
      await load();
    } catch (e: unknown) {
      showSnackbar(formatApiErrorMessage(e, '삭제에 실패'), 'error');
    }
  };

  if (!poolId) {
    return (
      <Alert severity="info">시설 선택 후 경광등·부저 등록.</Alert>
    );
  }

  return (
    <Box>
      <Alert
        severity={isThisModeActive ? 'success' : 'info'}
        sx={{ mb: 2 }}
        action={
          onActiveLinkModeChange ? (
            <Button
              color="inherit"
              size="small"
              disabled={isThisModeActive || modeSaving}
              onClick={() => void handleApplyThisMode()}
            >
              {modeSaving ? '저장 중…' : isThisModeActive ? '현재 적용 중' : '이 방식으로 적용'}
            </Button>
          ) : undefined
        }
      >
        {isServerA
          ? '경광등/부저 A: 현장 게이트웨이가 TCP 서버로 대기하고, 릴레이 보드가 접속. 채널별 hex만 다름.'
          : '경광등/부저(기존): 게이트웨이/서버가 릴레이 IP로 접속해 명령을 보냄.'}
        {!isThisModeActive
          ? ' — 현재 시설에는 다른 방식이 적용되어 있어, 관제·알람은 이 탭 장비를 쓰지 않음.'
          : ' — 관제·알람은 이 탭에 등록된 장비를 사용'}
      </Alert>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="경광등" />
        <Tab label="부저" />
      </Tabs>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <TextField
          label="테스트 울림(초)"
          size="small"
          type="number"
          inputProps={{ min: 1, max: 300 }}
          value={testDurationSec}
          onChange={(e) => setTestDurationSec(e.target.value)}
          sx={{ width: 140 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {DEVICE_TYPE_LABEL[deviceType]} 추가
        </Button>
      </Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>표시명</TableCell>
                <TableCell>{isServerA ? 'Listen' : '호스트'}</TableCell>
                <TableCell>포트</TableCell>
                <TableCell>프로토콜</TableCell>
                <TableCell>활성</TableCell>
                <TableCell align="right">관리</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    등록된 {DEVICE_TYPE_LABEL[deviceType]} 없음.
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={d.label?.trim() ? 500 : 400}>
                        {formatListDisplayName(d)}
                      </Typography>
                      {(d.label || '').trim() ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {d.display_label}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{d.host}</TableCell>
                    <TableCell>{d.port ?? `(기본 ${samplePort})`}</TableCell>
                    <TableCell>{(d.protocol || 'tcp').toUpperCase()}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={d.is_active ? 'ON' : 'OFF'}
                        color={d.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => void runTest({ deviceId: d.id })}
                        disabled={testingKey != null}
                        aria-label="테스트"
                        title="테스트 울림"
                      >
                        {testingKey === `row-${d.id}` ? (
                          <CircularProgress size={18} />
                        ) : (
                          <PlayArrowIcon fontSize="small" />
                        )}
                      </IconButton>
                      <IconButton size="small" onClick={() => openEdit(d)} aria-label="수정">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => void handleDelete(d)}
                        aria-label="삭제"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId != null ? `${DEVICE_TYPE_LABEL[deviceType]} 수정` : `${DEVICE_TYPE_LABEL[deviceType]} 등록`}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
            <TextField
              label="표시 이름 (권장)"
              helperText="이벤트 창·목록에 보이는 이름"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              fullWidth
            />
            <TextField
              label="위치 안내 (선택)"
              value={form.location_hint}
              onChange={(e) => setForm((f) => ({ ...f, location_hint: e.target.value }))}
              fullWidth
            />
            <TextField
              required
              label={isServerA ? 'Listen bind' : '호스트 (IP 또는 도메인)'}
              helperText={
                isServerA
                  ? '반드시 0.0.0.0. 릴레이 「서버 IP」에는 게이트웨이 PC의 LAN IP 입력(여기 넣으면 listen 실패).'
                  : '출고 기본 192.168.1.200 — 현장 LAN IP로 변경. 8채널 1대면 모든 행에 같은 IP'
              }
              value={form.host}
              onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
              fullWidth
            />
            <TextField
              label={`포트 (비우면 ${samplePort})`}
              value={form.port}
              onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="alert-device-protocol">프로토콜</InputLabel>
              <Select
                labelId="alert-device-protocol"
                label="프로토콜"
                value={isServerA ? 'tcp_server' : form.protocol}
                disabled={isServerA}
                onChange={(e: SelectChangeEvent) =>
                  setForm((f) => ({
                    ...f,
                    protocol: e.target.value as PoolAlertDeviceProtocol,
                  }))
                }
              >
                {isServerA ? (
                  <MenuItem value="tcp_server">TCP Server (A)</MenuItem>
                ) : (
                  [
                    <MenuItem key="tcp" value="tcp">
                      TCP
                    </MenuItem>,
                    <MenuItem key="http" value="http">
                      HTTP
                    </MenuItem>,
                  ]
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="alert-device-template">제조사 템플릿</InputLabel>
              <Select
                labelId="alert-device-template"
                label="제조사 템플릿"
                value={form.template_key}
                onChange={(e: SelectChangeEvent) => {
                  const key = e.target.value as string;
                  const tpl = templates.find((t) => t.key === key);
                  setForm((f) => applyTemplateToForm(f, key, tpl, form.device_type));
                }}
              >
                <MenuItem value="">
                  <em>사용자 정의 (수동 입력)</em>
                </MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.key} value={t.key}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {templates.find((t) => t.key === form.template_key)?.hint ? (
              <Alert severity="info" sx={{ gridColumn: '1 / -1' }}>
                {templates.find((t) => t.key === form.template_key)?.hint}
              </Alert>
            ) : null}
            <TextField
              label="연결 타임아웃(초)"
              value={form.socket_timeout_sec}
              onChange={(e) => setForm((f) => ({ ...f, socket_timeout_sec: e.target.value }))}
              fullWidth
            />
            <TextField
              label="정렬 순서"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
              }
              label="활성 (이벤트 창 목록에 표시)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.blink_enabled}
                  onChange={(e) => setForm((f) => ({ ...f, blink_enabled: e.target.checked }))}
                />
              }
              label="프로그램 Blink (장비에 자체 blink 없을 때 시작/중지 반복)"
            />
          </Box>
          {form.protocol === 'http' ? (
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="alert-http-method">HTTP 방식</InputLabel>
                <Select
                  labelId="alert-http-method"
                  label="HTTP 방식"
                  value={form.http_method}
                  onChange={(e: SelectChangeEvent) =>
                    setForm((f) => ({
                      ...f,
                      http_method: e.target.value as 'GET' | 'POST',
                    }))
                  }
                >
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="시작 경로 (위험)"
                helperText="호스트:포트 뒤에 붙는 URL 경로. 위험 알림 시 요청 (예: /relay.cgi?relay=1&on=1)"
                value={form.http_start_path_danger}
                onChange={(e) => setForm((f) => ({ ...f, http_start_path_danger: e.target.value }))}
                fullWidth
              />
              <TextField
                label="시작 경로 (경고)"
                helperText="경고 알림 시 요청할 URL 경로 (위험과 같게 둬도 됨)"
                value={form.http_start_path_warning}
                onChange={(e) => setForm((f) => ({ ...f, http_start_path_warning: e.target.value }))}
                fullWidth
              />
              <TextField
                label="중지 경로"
                helperText="끄기(중지) 시 요청할 URL 경로 (예: …&on=0). ※ 중기가 아니라 중지."
                value={form.http_stop_path}
                onChange={(e) => setForm((f) => ({ ...f, http_stop_path: e.target.value }))}
                fullWidth
              />
            </Box>
          ) : (
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="시작 명령 (위험)"
                helperText={
                  form.protocol === 'tcp_server'
                    ? '템플릿 Ch 선택 시 hex가 채워짐. 수정 가능.'
                    : '채널 구분은 이 문자열의 숫자(REL1 등)'
                }
                value={form.tcp_command_start_danger}
                onChange={(e) => setForm((f) => ({ ...f, tcp_command_start_danger: e.target.value }))}
                fullWidth
              />
              <TextField
                label="시작 명령 (경고)"
                helperText="경고 알림 시 보낼 TCP/hex 명령"
                value={form.tcp_command_start_warning}
                onChange={(e) => setForm((f) => ({ ...f, tcp_command_start_warning: e.target.value }))}
                fullWidth
              />
              <TextField
                label="중지 명령"
                helperText="끄기 시 보낼 명령"
                value={form.tcp_command_stop}
                onChange={(e) => setForm((f) => ({ ...f, tcp_command_stop: e.target.value }))}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.tcp_append_newline}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, tcp_append_newline: e.target.checked }))
                    }
                  />
                }
                label="명령 끝 LF 추가"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving || testingKey != null}>
            취소
          </Button>
          <Button
            variant="outlined"
            startIcon={testingKey === 'form' ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            onClick={() => void runTest({ fromForm: true })}
            disabled={saving || testingKey != null || !form.host.trim()}
          >
            테스트
          </Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving || testingKey != null}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PoolAlertDeviceManagement;
